import { formatCurrency } from 'common/FormatUtilities';
import { StorageKeys } from 'common/storage/constants';
import { DateString } from 'common/UrlDate';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { DishFirstFoodItemFragment, usePeopleAlsoOrderedLazyQuery } from 'src/gqlReactTypings.generated.d';
import { useDishModalActions as useExploreDishModalActions } from 'src/pages/consumer/dish-first/explore/store';
import { useGetFoodItemPath } from 'src/pages/consumer/order/components/ConsumerOrderDishSelectionItem/hooks';
import { ScallopedEdge } from 'src/shared/design-system/DishFirst/ScallopedEdge';
import { DynamicImage } from 'src/shared/design-system/DynamicImage';
import { ChevronLeft, ChevronRight } from 'src/shared/design-system/Icon';
import { Paragraph } from 'src/shared/design-system/Text';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useLocation } from 'src/shared/hooks/useLocation';
import { useScroller } from 'src/shared/hooks/useScroller';
import { Routes } from 'src/shared/Routes';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useDispatch, useSelector } from 'src/store';
import { incLineItemQuantity, setShefs } from 'src/store/cart/actions';
import { selectCart, selectLineItemIds } from 'src/store/cart/selectors';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import { DietaryTags } from 'web-common/src/shared/Enums';
import { Colors, FontSize, FontWeight } from 'web-common/src/shared/styles';
import { dishFirstFoodItemFragmentToCartShef } from '../../../../dish-first/explore/components/Segments/DishFirstModal/hooks/useFoodItemAvailability';
import {
  AddToCartButton,
  Container,
  ContentContainer,
  imageSizePx,
  LeftContainer,
  RightContainer,
  ScrollButton,
  ScrollButtons,
  SuggestionContainer,
  SuggestionImageContainer,
  SuggestionsContainer,
  TextContainer,
  TitleContainer,
} from './styles';

interface PeopleAlsoOrderedProps {
  deliveryDate: DateString | undefined;
  style?: React.CSSProperties;
  isEditingOrder?: boolean;
}

export const PeopleAlsoOrdered: React.FC<PeopleAlsoOrderedProps> = ({
  deliveryDate,
  isEditingOrder = false,
  ...restProps
}) => {
  const [zipCode] = useCachedZipCode();
  const [dietaryFilters] = useLocalStorage<DietaryTags[]>(StorageKeys.DIETARY_PREFERENCES, []);
  const cartFoodItemIds = useSelector((state) => selectLineItemIds(selectCart(state, isEditingOrder)));
  const { scrollRef, scrollLeft, scrollRight, canScrollLeft, canScrollRight, onScroll, reset } = useScroller(300);

  const [query, { data, previousData, loading }] = usePeopleAlsoOrderedLazyQuery({ fetchPolicy: 'cache-first' });
  useEffect(() => {
    if (!isEditingOrder && zipCode && deliveryDate) {
      query({ variables: { cartFoodItemIds, deliveryDate, zipCode, dietaryFilters } });
    }
  }, [isEditingOrder, cartFoodItemIds, deliveryDate, zipCode, dietaryFilters]);

  const memoedEmptyArray = useMemo(() => [], []);
  const recommendations = (loading ? previousData : data)?.peopleAlsoOrderedRecommendations || memoedEmptyArray;
  useEffect(() => {
    recommendations && reset();
  }, [recommendations, reset]);

  if (!deliveryDate || isEditingOrder || recommendations.length === 0) {
    return null;
  }

  return (
    <Container>
      <ContentContainer {...restProps}>
        <TitleContainer>
          <Paragraph color={Colors.GRAY_DARK_30} weight={FontWeight.BOLD} size={FontSize.M} margin='0'>
            You may also like...
          </Paragraph>
          <ScrollButtons>
            <ScrollButton disabled={!canScrollLeft} onClick={() => scrollLeft()}>
              <ChevronLeft width={16} height={16} />
            </ScrollButton>
            <ScrollButton disabled={!canScrollRight} onClick={() => scrollRight()}>
              <ChevronRight width={16} height={16} />
            </ScrollButton>
          </ScrollButtons>
        </TitleContainer>
        <SuggestionsContainer ref={scrollRef} className='no-scrollbar' onScroll={onScroll}>
          {recommendations.map((foodItem) => (
            <PeopleAlsoOrderedSuggestion key={foodItem.foodItemId} foodItem={foodItem} deliveryDate={deliveryDate} />
          ))}
        </SuggestionsContainer>
      </ContentContainer>
      <ScallopedEdge color={Colors.BUTTER_400} isFlipped />
    </Container>
  );
};

const useOpenDishDetailsModal = (shefMenuFoodItemPath: string) => {
  const history = useHistory();
  const location = useLocation();

  // HACK && FUTURE TODO: open dish modal should just be a url that we can navigate to
  // however the url doesn't work as direct link to user who hasn't visited that
  // food item before/recently... this is a hacky work around, since the try
  // will be run on every render, the same amount of hooks should be called per render
  // making this safe to do
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { openDishModal } = useExploreDishModalActions();

    const isShefMenuPage = locationInRoutes(location, [Routes.CONSUMER_ORDER_ONE_TIME]);
    if (!isShefMenuPage) return openDishModal;

    return (_params) => history.push(shefMenuFoodItemPath, { isShefPageInHistory: true, scrollToTop: false });
  } catch (err) {
    // assuming we aren't on explore page (desktop) or cart (mobile), and don't have context to open modal
    // as that's really the only reason this would throw in a type safe env
    console.warn(
      'Failed to load Dish First explore context for People Also Ordered suggestion. Consider providing this context in the appropriate component.',
      err
    );
    return () => history.push(shefMenuFoodItemPath, { isShefPageInHistory: true, scrollToTop: false });
  }
};

export const PeopleAlsoOrderedSuggestion = (props: { foodItem: DishFirstFoodItemFragment; deliveryDate: string }) => {
  const { foodItem, deliveryDate } = props;

  const location = useLocation();
  const match = useRouteMatch();
  const dispatch = useDispatch();

  const { foodItemPath } = useGetFoodItemPath({
    foodItemSlug: foodItem.slug,
    foodItemShefId: foodItem.shef.id,
    location,
    match,
    isEditingOrder: false,
  });
  const openDishModal = useOpenDishDetailsModal(foodItemPath);
  // TODO: this is opening to a edit-items url
  const handleViewDishDetailModal = useCallback(
    (foodItem: DishFirstFoodItemFragment) => () => {
      openDishModal({ foodItemSlug: foodItem.slug, shefId: foodItem.shef.id });
    },
    [foodItem]
  );

  const handleAddToCart =
    (dishFirstFoodItem: DishFirstFoodItemFragment) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      const foodItem = { id: dishFirstFoodItem.foodItemId, ...dishFirstFoodItem };
      dispatch(setShefs([dishFirstFoodItemFragmentToCartShef(foodItem)]));
      dispatch(incLineItemQuantity(false, { foodItem, deliveryDate, location: 'people-also-ordered' }));
    };

  return (
    <SuggestionContainer onClick={handleViewDishDetailModal(foodItem)}>
      <LeftContainer>
        <SuggestionImageContainer>
          <DynamicImage
            width={`${imageSizePx}px`}
            height={`${imageSizePx}px`}
            sourceHeight={imageSizePx}
            src={foodItem.imageUrl ?? ''}
            alt={foodItem.name ?? ''}
          />
        </SuggestionImageContainer>
        <TextContainer>
          <Paragraph
            className='truncate'
            color={Colors.GRAY_DARK_30}
            weight={FontWeight.SEMI_BOLD}
            size={FontSize.S}
            margin='0'
            title={foodItem.name || ''}>
            {foodItem.name}
          </Paragraph>
          <Paragraph color={Colors.GRAY_DARK_30} size={FontSize.XS} margin='0' weight={FontWeight.NORMAL}>
            {formatCurrency(foodItem.price)}
          </Paragraph>
        </TextContainer>
      </LeftContainer>
      <RightContainer>
        <AddToCartButton onClick={handleAddToCart(foodItem)}>
          <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M16 7.96154C16 8.5 15.5769 8.88462 15.0769 8.88462H8.92308V15.0385C8.92308 15.5769 8.5 16 8 16C7.46154 16 7.07692 15.5769 7.07692 15.0385V8.88462H0.923077C0.384615 8.88462 0 8.5 0 8C0 7.46154 0.384615 7.03846 0.923077 7.03846H7.07692V0.884615C7.07692 0.384615 7.46154 0 8 0C8.5 0 8.92308 0.384615 8.92308 0.884615V7.03846H15.0769C15.5769 7.03846 16 7.46154 16 7.96154Z'
              fill='currentColor'
            />
          </svg>
        </AddToCartButton>
      </RightContainer>
    </SuggestionContainer>
  );
};
